/* eslint react/jsx-max-props-per-line: 0 */
import React from 'react';
import PropTypes from 'prop-types';

import { toThousandsSeparator } from 'helpers/NumberFormats';
import styles from 'styles/components/account/rewardsProgress.scss';

const EMPTY = '#ebeff4';
const SILVER = '#939aaa';
const GOLD = '#6c85a7';
const PLATINUM = '#1f6091';

const ARC_THRESHOLD = 1200;

const SIVLER_TIER = 1199;
const GOLD1_TIER = 2400; // Upper left circle
const GOLD2_TIER = 3600; // Upper circle
const GOLD3_TIER = 4800; // Upper right circle
const PLATINUM_TIER = 5999;

const EASE_IN = '0.4, 0, 0.2, 1';

const tierColorMap = {
  1: SILVER,
  2: GOLD,
  3: PLATINUM
};

const RewardsProgress = ({ size = 350, rewardsInfo: { tierPoints, tierLabel, tier, pointsToNextTier } }, { testId }) => {
  const nextTierLabel = (tier === 1) ? 'Gold' : (tier === 2) ? 'Platinum' : null;

  const silverOffset = tierPoints >= ARC_THRESHOLD ? 1 : tierPoints / ARC_THRESHOLD;
  const silverGoldOffset = (tierPoints - ARC_THRESHOLD) >= ARC_THRESHOLD ? 1 : (tierPoints - ARC_THRESHOLD) / ARC_THRESHOLD;
  const goldOffset = tierPoints - (ARC_THRESHOLD * 2) >= ARC_THRESHOLD ? 1 : (tierPoints - (ARC_THRESHOLD * 2)) / ARC_THRESHOLD;
  const goldPlatinumOffset = tierPoints - (ARC_THRESHOLD * 3) >= ARC_THRESHOLD ? 1 : (tierPoints - (ARC_THRESHOLD * 3)) / ARC_THRESHOLD;
  const platinumOffset = tierPoints - (ARC_THRESHOLD * 4) >= ARC_THRESHOLD ? 1 : (tierPoints - (ARC_THRESHOLD * 4)) / ARC_THRESHOLD;

  return (
    <svg
      data-test-id={testId('rewardsInfo')}
      width={size}
      height={size}
      viewBox="0 0 210 210">
      <g fill="none" strokeWidth="9" transform="translate(105,105) rotate(29.8)">
        <defs>
          <linearGradient
            id="silver"
            gradientUnits="objectBoundingBox"
            x1="1"
            y1="0"
            x2="0"
            y2="0">
            <stop
              offset="0"
              stopColor={SILVER}>
              <animate
                attributeName="offset"
                dur="0.6s"
                from="0"
                to={silverOffset}
                fill="freeze"
                calcMode="spline"
                keySplines={EASE_IN} />
              {tierPoints > SIVLER_TIER && <animate
                attributeName="offset"
                begin="0.6s"
                dur="0.3s"
                from="1"
                to="0"
                fill="freeze"
                calcMode="spline"
                keySplines={EASE_IN} />}
            </stop>
            <stop
              offset="0"
              stopColor={EMPTY}>
              {tierPoints > SIVLER_TIER && <animate
                attributeName="offset"
                begin="0.6s"
                dur="0.3s"
                from="0"
                to="1"
                fill="freeze" />}
              {tierPoints > SIVLER_TIER && <animate
                attributeName="stop-color"
                begin="0.6s"
                dur="0.3s"
                from={SILVER}
                to={GOLD}
                fill="freeze"
                calcMode="spline"
                keySplines={EASE_IN} />}
            </stop>
          </linearGradient>
          <linearGradient
            id="silverGold"
            gradientUnits="objectBoundingBox"
            x1="0"
            y1="1"
            x2="0"
            y2="0">
            <stop
              offset="0"
              stopColor={EMPTY}>
              {tierPoints > SIVLER_TIER && <animate
                attributeName="offset"
                begin="0.6s"
                dur="0.3s"
                from="0"
                to={silverGoldOffset}
                fill="freeze"
                calcMode="spline"
                keySplines={EASE_IN} />}
              {tierPoints > SIVLER_TIER && <animate
                attributeName="stop-color"
                begin="0.6s"
                dur="0.3s"
                from={SILVER}
                to={GOLD}
                fill="freeze"
                calcMode="spline"
                keySplines={EASE_IN} />}
            </stop>
            <stop
              offset="0"
              stopColor={EMPTY}>
              {tierPoints > SIVLER_TIER && <animate
                attributeName="stop-color"
                begin="0.9s"
                dur="0.3s"
                from={GOLD}
                to={GOLD}
                fill="freeze"
                calcMode="spline"
                keySplines={EASE_IN} />}
            </stop>
          </linearGradient>
          <linearGradient
            id="gold"
            gradientUnits="objectBoundingBox"
            x1="0"
            y1="1"
            x2="1"
            y2="0">
            <stop
              offset="0"
              stopColor={EMPTY}>
              {tierPoints > GOLD1_TIER && <animate
                attributeName="offset"
                begin="0.9s"
                dur="0.3s"
                from="0"
                to={goldOffset}
                fill="freeze"
                calcMode="spline"
                keySplines={EASE_IN} />}
              {tierPoints > GOLD1_TIER && <animate
                attributeName="stop-color"
                begin="0.9s"
                dur="0.3s"
                from={GOLD}
                to={GOLD}
                fill="freeze"
                calcMode="spline"
                keySplines={EASE_IN} />}
            </stop>
            <stop
              offset="0"
              stopColor={EMPTY}>
              {tierPoints > SIVLER_TIER && <animate
                attributeName="stop-color"
                begin="1.2s"
                dur="0.3s"
                from={GOLD}
                to={GOLD}
                fill="freeze"
                calcMode="spline"
                keySplines={EASE_IN} />}
            </stop>
          </linearGradient>
          <linearGradient
            id="goldPlatinum"
            gradientUnits="objectBoundingBox"
            x1="0"
            y1="0"
            x2="1"
            y2="1">
            <stop
              offset="1"
              stopColor={EMPTY}>
              {tierPoints > GOLD2_TIER && <animate
                attributeName="offset"
                begin="1.2s"
                dur="0.3s"
                from="0"
                to={goldPlatinumOffset}
                fill="freeze"
                calcMode="spline"
                keySplines={EASE_IN} />}
              {tierPoints > GOLD2_TIER && <animate
                attributeName="stop-color"
                begin="1.2s"
                dur="0.3s"
                from={GOLD}
                to={GOLD}
                fill="freeze"
                calcMode="spline"
                keySplines={EASE_IN} />}
              {tierPoints > GOLD3_TIER && <animate
                attributeName="offset"
                begin="1.5s"
                dur="0.3s"
                from="1"
                to="0"
                fill="freeze"
                calcMode="spline"
                keySplines={EASE_IN} />}
            </stop>
            <stop
              offset="0"
              stopColor={EMPTY}>
              {tierPoints > GOLD3_TIER && <animate
                attributeName="offset"
                begin="1.5s"
                dur="0.3s"
                from="0"
                to="1"
                fill="freeze"
                calcMode="spline"
                keySplines={EASE_IN} />}
              {tierPoints > GOLD3_TIER && <animate
                attributeName="stop-color"
                begin="1.5s"
                dur="0.3s"
                from={PLATINUM}
                to={PLATINUM}
                fill="freeze"
                calcMode="spline"
                keySplines={EASE_IN} />}
            </stop>
          </linearGradient>
          <linearGradient
            id="platinum"
            gradientUnits="objectBoundingBox"
            x1="1"
            y1="0"
            x2="1"
            y2="1">
            <stop
              offset="1"
              stopColor={EMPTY}>
              {tierPoints > GOLD3_TIER && <animate
                attributeName="offset"
                begin="1.5s"
                dur="0.6s"
                from="0"
                to={platinumOffset}
                fill="freeze"
                calcMode="spline"
                keySplines={EASE_IN} />}
              {tierPoints > GOLD3_TIER && <animate
                attributeName="stop-color"
                begin="1.5s"
                dur="0.6s"
                from={PLATINUM}
                to={PLATINUM}
                fill="freeze"
                calcMode="spline"
                keySplines={EASE_IN} />}
            </stop>
            <stop
              offset="0"
              stopColor={EMPTY} />
          </linearGradient>
        </defs>
        <path
          d="M 0,100 A 100,100 0 0,1 -87.2,49"
          stroke="url(#silver)" />
        <path
          d="M -86.6,50 A 100,100 0 0,1 -86.5,-50.1"
          stroke="url(#silverGold)" />
        <path
          d="M -86.6,-49.9 A 100,100 0 0,1 0.2,-100"
          stroke="url(#gold)" />
        <path
          d="M -0.1,-100 A 100,100 0 0,1 86.7,-49.9"
          stroke="url(#goldPlatinum)" />
        <path
          d="M 86.5,-50.2 A 100,100 0 0,1 86.6,50"
          stroke="url(#platinum)" />
        <circle
          cx={0}
          cy={100}
          r={4.6}
          fill={EMPTY}>
          <animate
            attributeName="fill"
            dur="1ms"
            from={EMPTY}
            to={SILVER}
            fill="freeze" />
        </circle>
        <circle
          cx={87}
          cy={49}
          r={4.6}
          fill={EMPTY}>
          {tierPoints > PLATINUM_TIER && <animate
            attributeName="fill"
            begin="2.07s"
            dur="1ms"
            from={EMPTY}
            to={PLATINUM}
            fill="freeze" />}
        </circle>
      </g>
      <text
        x="105"
        y="60"
        textAnchor="middle"
        className={styles.tierPointsTitle}>
        <tspan>Tier Points</tspan>
      </text>
      <text
        x="105"
        y="106"
        textAnchor="middle"
        className={styles.rewardsPoints}>
        <tspan data-test-id={testId('tierPoints')}>{toThousandsSeparator(tierPoints)}</tspan>
      </text>
      <line
        x1="50"
        y1="115"
        x2="160"
        y2="115"
        stroke={EMPTY}
        strokeWidth={3} />
      <text
        x="105"
        y="137"
        textAnchor="middle"
        className={styles.currentTier}>
        <tspan fill={tierColorMap[tier]} data-test-id={testId('tierLevel')}>{tierLabel}</tspan>
      </text>
      {nextTierLabel && <text
        textAnchor="middle"
        x="105"
        y="165"
        className={styles.pointsToNextTier}>
        <tspan data-test-id={testId('pointsToNextTier')}>{pointsToNextTier} points till <tspan data-test-id={testId('nextTierLevel')}>{nextTierLabel}</tspan></tspan>
      </text>}
    </svg>
  );
};

RewardsProgress.contextTypes = {
  testId: PropTypes.func
};

RewardsProgress.propTypes = {
  rewardsInfo: PropTypes.object
};

export default RewardsProgress;
